import { useState, type ReactNode } from 'react'
import Sidebar from './Sidebar'
import OfflineIndicator from './OfflineIndicator'
import CommandPalette from './CommandPalette'
import SessionTerminal from './SessionTerminal'
import LowCreditsBanner from './LowCreditsBanner'
import { TerminalProvider } from '../lib/terminalContext'
import { MODELS } from '../lib/models'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [selectedModelId, setSelectedModelId] = useState(MODELS[0].id)

  return (
    <TerminalProvider>
      <LayoutInner
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        selectedModelId={selectedModelId}
        setSelectedModelId={setSelectedModelId}
      >
        {children}
      </LayoutInner>
    </TerminalProvider>
  )
}

interface LayoutInnerProps {
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  selectedModelId: string
  setSelectedModelId: (id: string) => void
  children: ReactNode
}

function LayoutInner({
  sidebarOpen,
  setSidebarOpen,
  selectedModelId,
  setSelectedModelId,
  children,
}: LayoutInnerProps) {
  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      <OfflineIndicator />

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        selectedModelId={selectedModelId}
        onModelChange={setSelectedModelId}
      />

      <button
        onClick={() => setSidebarOpen(true)}
        className="fixed top-4 left-4 z-30 p-2 rounded-lg bg-bg-card border border-border md:hidden cursor-pointer hover:bg-bg-elevated transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      <main
        className="md:ml-[var(--sidebar-width)] min-h-screen"
        style={{
          // Reserve room for the PromptBar (~100px) AND the SessionTerminal
          // (32px collapsed / 320px expanded). Driven by the CSS variable
          // terminalContext sets so content never hides behind the dock.
          paddingBottom: 'calc(var(--terminal-height, 32px) + 100px)',
        }}
      >
        <div className="p-6 max-md:pt-16">{children}</div>
      </main>

      <CommandPalette />
      <LowCreditsBanner />
      <SessionTerminal />
    </div>
  )
}
